import { load } from 'recaptcha-v3'

import type { FieldValues } from 'react-hook-form'

import { getBackendHost, track } from '@segment/tracking/scripts'

export type FormStatus = `idle` | `success` | `error`

/**
 * Handle form error.
 * Fires `Form Failed` track event.
 * @param {Object} error
 */
const handleError = async (error: { status: string; statusText: string }): Promise<FormStatus> => {
  try {
    await track(`Form Failed`, {
      type: error?.status,
      error: error?.statusText,
    })
  } catch (err) {
    return `error`
  }

  // Returns FormStatus = `error` to display error message to the user.
  return `error`
}

/**
 * Contact Billing form onSubmit logic.
 * send a backend contact ticket, or track form failure.
 * @param {FieldValues} formData
 */
const submitContactBillingForm = async (formData: FieldValues): Promise<FormStatus> => {
  try {
    // Get recaptcha token for posting to `segment-site-backend`.
    // See https://developers.google.com/recaptcha/docs/v3 for more info.
    const recaptchaClientKey = process.env.RECAPTCHA_GCP_CLIENT_KEY || ``
    const recaptcha = await load(recaptchaClientKey)
    const token = await recaptcha.execute(`form`)

    // Set up the payload.
    const contactBillingPayload = {
      formId: ``,
      formType: `billing`,
      formFields: {
        name: formData.name,
        email: formData.email,
        workspaceSlug: formData.slug,
        description: formData.help,
        interestedInFunctions: false,
        interestedInPrivacy: false,
      },
      recaptchaToken: token,
    }

    // Post data to `segment-site-backend` contact ticket endpoint.
    const status = await fetch(
      `${getBackendHost(window.location.host, window.location.protocol)}/backend/contact/ticket`,
      {
        method: `POST`,
        headers: {
          'Content-Type': `application/json`,
        },
        body: JSON.stringify(contactBillingPayload),
      }
    )
      .then((res) => res.json())
      .then((body) => {
        // Code 400 is returned when there is a recaptcha error or a zendesk creation ticket error.
        if (body.status !== 400) {
          // Returns FormStatus = `success` to display success message to the user.
          return `success`
        }

        // Returns FormStatus = `error` to display error message to the user.
        return handleError({ status: body.status, statusText: body.message })
      })
      .catch(handleError)

    return status
  } catch (error: any) {
    return handleError(error)
  }
}

export default submitContactBillingForm
